import React from 'react'
import { FormContext, FormSectionContext } from 'src/contexts'
import { checkObject, FormObserver } from 'src/util'
import { useIntl } from 'react-intl'
import { hasValidationError } from 'src/services'

/**
 * A hook responsible of the initialization of a form
 *
 * @param {Object} options
 * @param {Function} options.onSubmit - the handler to be called on form submit
 * @param {Function} options.validate - the form validator, called before submission
 * @param {Boolean} options.disabled  - whether or not the form and its fields should be disabled
 * @param {any} options.initialValues - the initial values of the form
 * @param {Array} options.dependentFields - a list of fields dependent with each other (error should be resetted when the dependency is updated)
 */
export default function useForm({ onSubmit, validate, disabled, initialValues, dependentFields }) {
  // create form observer
  const form = React.useMemo(
    () => new FormObserver({ initialValues }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  // update form when props changes
  React.useEffect(
    () => form.isDisabled.set(disabled),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [disabled]
  )
  React.useEffect(
    () => {
      form.validate = validate
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [validate]
  )
  React.useEffect(
    () => {
      form.onSubmit = onSubmit
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [onSubmit]
  )
  React.useEffect(
    () => {
      if (initialValues !== form.initialValues.get()) form.initialValues.set(initialValues)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialValues]
  )

  // initialize dependent fields
  useDependentFields(dependentFields, form)

  // return memoized form
  return form
}

/**
 * Create form subscriptions related to dependent fields
 */
function useDependentFields(fields = [], form) {
  React.useEffect(
    () => {
      const unsubscribers = fields.map(({ triggers, updateFields }) =>
        form.values.subscribe(() => updateFields.forEach(field => form.values.notify(field)), ...triggers)
      )
      return () => unsubscribers.forEach(unsubscribe => unsubscribe())
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [fields]
  )
}

/**
 * Get a form value at a specific path
 */
export function useFormValue(path, defaultValue) {
  const { values } = FormContext.use()
  return values.useValue(path, defaultValue)
}

/**
 * Get a form value at a specific path
 */
export function useFormInitialValue(path, defaultValue) {
  const { initialValues } = FormContext.use()
  return initialValues.useValue(path, defaultValue)
}

/**
 * Get a form value and updater at a specific path
 */
export function useFormState(path, defaultValue) {
  const { values } = FormContext.use()
  return values.useState(path, defaultValue)
}

/**
 * Get a form path, related to provided name and registered form sections
 */
export function useFormSectionPath(name) {
  const section = FormSectionContext.use()
  return React.useMemo(() => [section.path, name].filter(Boolean).join('.'), [section.path, name])
}

/**
 * Get the disabled status of the form
 */
export function useIsFormDisabled() {
  const { isDisabled } = FormContext.use()
  return isDisabled.useValue()
}

/**
 * Get the disabled status of a form section
 */
export function useIsFormSectionDisabled() {
  const section = FormSectionContext.use()
  return useIsFormDisabled() || section.disabled
}

/**
 * Get the submitting status of the form
 */
export function useIsFormSubmitting() {
  const { isSubmitting } = FormContext.use()
  return isSubmitting.useValue()
}

/**
 * Get the valid status of a specific path in the form
 */
export function useIsFormValid(path) {
  const { errors } = FormContext.use()
  const error = errors.useValue(path)
  return React.useMemo(() => !hasValidationError(error), [error])
}

/**
 * Get the dirty status of a specific path in the form
 */
export function useIsFormDirty(path) {
  const { dirties } = FormContext.use()
  const value = dirties.useValue(path)
  return React.useMemo(() => checkObject(value, Boolean), [value])
}

/**
 * Get the translated error of a specific path in the form
 */
export function useFormError(path) {
  const { errors } = FormContext.use()
  const error = errors.useValue(path)
  const intl = useIntl()
  return React.useMemo(() => (error && error.translate ? error.translate(intl) : error), [error, intl])
}
